import { memo } from 'react';

import { Chip, useTheme } from '@mui/material';

import { formatTimeAgo } from '@modules/_shared/utils/formatting';
import type { CmaListing } from '@modules/reports/cma-reports/step-listing-compare/types';
import { useLocale } from '@shared/hooks/locale';

type ListingStatusChipProps = {
  listing: Partial<Pick<CmaListing, 'valid_from' | 'offline_since'>>;
  variant?: 'status' | 'duration';
  shortLabels?: boolean;
  compact?: boolean;
};

const useFormatTimeAgoWithTooltip = () => {
  const { t, dateLocale } = useLocale();

  return (date: string, suffix?: boolean) => {
    const { timeAgo } = formatTimeAgo(
      date,
      suffix ?? false,
      false,
      t,
      dateLocale,
      true,
      true,
    );
    return { timeAgo };
  };
};

const ListingStatusChip = ({
  listing,
  variant = 'status',
  shortLabels = false,
  compact = false,
}: ListingStatusChipProps) => {
  const { t } = useLocale();
  const theme = useTheme();
  const formatTimeAgoWithTooltip = useFormatTimeAgoWithTooltip();
  const isOnline = listing.offline_since == null;
  const isStatusVariant = variant === 'status';

  if (listing.valid_from == null) {
    return null;
  }

  const getStatusInfo = () => {
    const { valid_from, offline_since } = listing;

    if (isOnline) {
      if (isStatusVariant) {
        return {
          text: t('Online'),
          color: (theme.vars ?? theme).palette.success.main,
        };
      }
      const { timeAgo } = formatTimeAgoWithTooltip(valid_from);
      const { timeAgo: timeAgoSuffix } = formatTimeAgoWithTooltip(
        valid_from,
        true,
      );

      if (shortLabels) {
        return {
          text: t('{{date}}', { date: timeAgoSuffix }),
          color: (theme.vars ?? theme).palette.success.main,
        };
      }

      return {
        text: t('{{date}} on the market', { date: timeAgo }),
        color: (theme.vars ?? theme).palette.grey[500],
      };
    }

    if (isStatusVariant) {
      const { timeAgo } = formatTimeAgoWithTooltip(offline_since);
      const { timeAgo: timeAgoSuffix } = formatTimeAgoWithTooltip(
        offline_since,
        true,
      );

      if (shortLabels) {
        return {
          text: t('{{date}}', {
            date: timeAgoSuffix,
          }),
          color: (theme.vars ?? theme).palette.error.main,
        };
      }

      return {
        text: t('offlineSince {{date}}', { date: timeAgo }),
        color: (theme.vars ?? theme).palette.error.main,
      };
    }

    const validFromDate = new Date(valid_from);
    const offlineDate = new Date(offline_since);
    const durationMs = offlineDate.getTime() - validFromDate.getTime();
    const durationHours = Math.floor(durationMs / (1000 * 60 * 60));

    const adjustedDate = new Date();
    adjustedDate.setHours(adjustedDate.getHours() - durationHours);

    const { timeAgo } = formatTimeAgoWithTooltip(adjustedDate.toISOString());

    if (shortLabels) {
      return {
        text: t('{{date}} online', { date: timeAgo }),
        color: (theme.vars ?? theme).palette.grey[500],
      };
    }

    return {
      text: t('{{date}} on the market', { date: timeAgo }),
      color: (theme.vars ?? theme).palette.grey[500],
    };
  };

  const { text, color } = getStatusInfo();

  return (
    <Chip
      size="small"
      label={text}
      sx={{
        ...(compact
          ? {
              height: 22,
              fontSize: '12px',
              fontWeight: 600,
              borderRadius: '6px',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.16)',
              '& .MuiChip-label': { px: 1 },
            }
          : {}),
        backgroundColor: color,
        color: '#fff',
      }}
    />
  );
};

export default memo(ListingStatusChip);
